import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AdminNavbar from 'components/Navbars/AdminNavbar'
import Select from 'components/Inputs/Select'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import productValidator from 'validators/Admin/productValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Container,
  Form,
  FormField,
  Title,
  Input,
  Row,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditProduct () {
  const [errorMessage, setErrorMessage] = useState (null)
  const [product, setProduct] = useState (null)
  const [categories, setCategories] = useState ([])
  const [loading, setLoading] = useState (true)
  const [showDeleteDialog, setShowDeleteDialog] = useState (false)
  const [showRestoreDialog, setShowRestoreDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const token = AuthService.getToken ()
  const { id: product_id } = useParams ()
  const navigate = useNavigate ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data: categories } = await cutileiApi.get ('/product_categories', requestConfig)
      const { data: product } = await cutileiApi.get (`/products/${product_id}`, requestConfig)

      setProduct (product)
      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const deleteProduct = async () => {
    try {
      await cutileiApi.delete (`/products/${product_id}`, requestConfig)
      navigate ('/admin/config', { replace: true })
    } catch (error) {
      logApiError (error)
    }
  }

  const restoreProduct = async () => {
    try {
      await cutileiApi.post (`/products/${product_id}/reactivate`, {}, requestConfig)
      navigate ('/admin/config', { replace: true })
    } catch (error) {
      logApiError (error)
    }
  }

  const toggleDeleteDialog = () => setShowDeleteDialog (!showDeleteDialog)
  const toggleRestoreDialog = () => setShowRestoreDialog (!showRestoreDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      <AdminNavbar/>
      {loading ? <Loading/> : (
        <Formik
          validationSchema={productValidator}
          initialValues={{
            'name': product.name,
            'description': product.description ?? '',
            'category': {
              label: product.category.name,
              value: product.category.id
            }
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const { category, ...product } = values

            try {
              await cutileiApi.put (`/services/${product_id}`, {
                productCategoryId: category.value,
                ...product
              }, requestConfig)

              setSubmitting (false)
              toggleOkDialog ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
              <Title>Editar produto</Title>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Nome'
                    value={values.name}
                    onChange={handleChange ('name')} 
                    onBlur={handleBlur ('name')}
                  />
                  {touched.name && errors.name && (
                    <WarningText>
                      {errors.name}
                    </WarningText>
                  )}
                </FormField>
                <FormField>
                  <Select
                    name='category'
                    placeholder='Categoria...'
                    value={values.category}
                    options={categories}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    error={errors.category}
                    touched={touched.category}
                  />
                  {touched.category && errors.category && (
                    <WarningText>
                      {errors.category}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Descrição do serviço'
                    value={values.description}
                    onChange={handleChange ('description')} 
                    onBlur={handleBlur ('description')}
                  />
                  {touched.description && errors.description && (
                    <WarningText>
                      {errors.description}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              {errorMessage && (
                <ErrorContainer>
                  <InfoText>{errorMessage}</InfoText>
                </ErrorContainer>
              )}
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={180}
                style={{marginBottom: 10}}
              />
              {product.deleted ? (
                <SecondaryButton
                  label='Reativar produto'
                  onClick={toggleRestoreDialog}
                  width={180}
                />
              ) : (
                <MainButton
                  label='Excluir produto'
                  onClick={toggleDeleteDialog}
                  dangerous={true}
                  width={180}
                />
              )}
            </Form>
          )}
        </Formik>
      )}
      <AlertDialog
        visible={showDeleteDialog}
        title='Atenção!'
        message='Deseja realmente excluir este produto?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteProduct}
        onClose={toggleDeleteDialog}
        dangerous
      />
      <AlertDialog
        visible={showRestoreDialog}
        title='Atenção!'
        message='Deseja realmente reativar este produto?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={restoreProduct}
        onClose={toggleRestoreDialog}
      />
      <AlertDialog
        visible={showOkDialog}
        title='Sucesso'
        message='Alterações salvas!'
        confirmText='Ok'
        onConfirm={() => navigate ('/admin/config', { replace: true })}
        onClose={() => navigate ('/admin/config', { replace: true })}
      />
    </Container>
  )
}
